<div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
  <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">
    <h1 class="text-xl font-bold tracking-tight text-gray-900">Meine Verknüpfungen</h1>

    @if (loading()) {
      <div class="flex items-center justify-center py-20" data-test="contacts-loading">
        <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
             xmlns="http://www.w3.org/2000/svg">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
        </svg>
      </div>
    } @else if (errorMessage()) {
      <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm" data-test="contacts-error">
        <p class="text-sm font-semibold text-red-600">{{ errorMessage() }}</p>
        <button (click)="load()" data-test="contacts-retry"
                class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">
          Erneut versuchen
        </button>
      </div>
    } @else {

      <!-- The wizard takes the whole surface while it is open: a guided flow next to the list it is about to
           change would be two sources of truth on one screen. -->
      @if (wizard(); as active) {
        <app-contact-link-wizard [channel]="active.channel" [channelLabel]="label(active.channel)"
                                 [purpose]="active.purpose" [dismissable]="true"
                                 (completed)="closeWizard(true)"
                                 (dismissed)="closeWizard(false)"></app-contact-link-wizard>
      } @else {

        <p class="text-sm text-gray-500 leading-relaxed">
          Hier sehen Sie, welche Messenger-Konten mit Ihrem CheqMe-Konto verknüpft sind und welche Arbeitgeber
          diese Verknüpfung nutzen dürfen. Sie können eine Freigabe jederzeit aufheben.
        </p>

        @if (links().length === 0) {
          <p class="text-sm text-gray-500 italic py-10 border-2 border-dashed border-gray-100 rounded-2xl text-center"
             data-test="contacts-empty">
            Sie haben noch keinen Kontaktkanal verknüpft.
          </p>
        } @else {
          <ul class="flex flex-col gap-3" data-test="contacts-list">
            @for (link of links(); track link.linkId) {
              <li class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm"
                  [attr.data-test]="'contact-link-' + link.channel">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-base font-bold text-gray-800 tracking-tight">{{ label(link.channel) }}</p>
                    <p class="mt-0.5 text-sm text-gray-600 break-all">{{ link.displayName }}</p>
                    <p class="mt-1 text-xs text-gray-500">
                      Verknüpft am {{ link.linkedAtUtc | date: 'dd.MM.yyyy' }}<!--
                      -->@if (link.lastVerifiedUtc) {<span> · zuletzt bestätigt am {{ link.lastVerifiedUtc | date: 'dd.MM.yyyy' }}</span>}
                    </p>
                  </div>
                  <div class="flex shrink-0 flex-wrap gap-2">
                    <button type="button" (click)="reverify(link)"
                            [attr.data-test]="'contact-reverify-' + link.channel"
                            class="inline-flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95">
                      Erneut bestätigen
                    </button>
                    <button type="button" (click)="askUnlink(link.linkId)"
                            [attr.data-test]="'contact-unlink-' + link.channel"
                            class="inline-flex items-center justify-center rounded-2xl border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 shadow-sm transition-all duration-200 hover:bg-red-100 active:scale-95">
                      Verknüpfung aufheben
                    </button>
                  </div>
                </div>

                <!-- Geteilt mit: the consent record, which exists nowhere else in the app. -->
                <div class="mt-3 border-t border-gray-100 pt-3">
                  <p class="text-xs font-bold uppercase tracking-widest text-gray-500">Geteilt mit</p>
                  @if (link.sharedWith.length === 0) {
                    <p class="mt-1 text-sm text-gray-500">
                      Noch mit keinem Arbeitgeber geteilt.
                    </p>
                  } @else {
                    <ul class="mt-1 flex flex-col gap-1">
                      @for (share of link.sharedWith; track share.companyId) {
                        <li class="flex flex-wrap items-baseline gap-x-2 text-sm text-gray-600">
                          <span class="font-semibold text-gray-800">{{ share.companyName }}</span>
                          <span class="text-xs text-gray-500">freigegeben am {{ share.sharedAtUtc | date: 'dd.MM.yyyy' }}</span>
                        </li>
                      }
                    </ul>
                  }
                </div>

                @if (confirmingUnlink() === link.linkId) {
                  <div class="mt-3 rounded-2xl border border-red-100 bg-red-50 p-3"
                       [attr.data-test]="'contact-unlink-confirm-' + link.channel" role="status">
                    <p class="text-sm font-bold text-red-700 tracking-tight">Verknüpfung wirklich aufheben?</p>
                    <p class="mt-1 text-sm text-red-700 leading-relaxed">
                      Ihre Arbeitgeber können Sie danach nicht mehr über {{ label(link.channel) }} erreichen.
                      Verlangt ein Arbeitgeber diesen Kanal, werden Sie sofort erneut zur Verknüpfung
                      aufgefordert.
                    </p>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button type="button" (click)="confirmUnlink(link)" [disabled]="unlinking() === link.linkId"
                              [attr.data-test]="'contact-unlink-confirmed-' + link.channel"
                              class="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-red-700 disabled:opacity-60">
                        @if (unlinking() === link.linkId) { Wird aufgehoben… } @else { Ja, aufheben }
                      </button>
                      <button type="button" (click)="askUnlink(link.linkId)"
                              class="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-600 border border-gray-200 transition-all duration-200 hover:bg-gray-50">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                }
              </li>
            }
          </ul>
        }

        <!-- Channels on offer that are not linked yet. An unavailable one is SHOWN as unavailable rather than
             hidden, so "why is this not here?" never has to be asked. -->
        @if (linkableChannels.length > 0) {
          <div class="flex flex-col gap-2" data-test="contacts-linkable">
            <h2 class="text-sm font-bold uppercase tracking-widest text-gray-500">Weitere Kanäle</h2>
            @for (channel of linkableChannels; track channel.channel) {
              <div class="flex flex-wrap items-center justify-between gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm"
                   [attr.data-test]="'contact-linkable-' + channel.channel">
                <div class="min-w-0">
                  <p class="text-base font-bold text-gray-800 tracking-tight">{{ channel.displayName }}</p>
                  @if (!channel.available) {
                    <p class="mt-0.5 text-sm text-gray-500"
                       [attr.data-test]="'contact-unavailable-' + channel.channel">
                      {{ unavailableMessage(channel) }}
                    </p>
                  }
                </div>
                @if (channel.available) {
                  <button type="button" (click)="linkChannel(channel)"
                          [attr.data-test]="'contact-link-start-' + channel.channel"
                          class="shrink-0 inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-theme-primary-700 active:scale-95">
                    Jetzt verknüpfen
                  </button>
                }
              </div>
            }
          </div>
        }
      }
    }
  </div>
</div>
